import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { PageLayout } from '../components/PageLayout';
import { Award, Users, Target, Heart, Handshake, Star } from 'lucide-react';

const aboutPhotoSrc = `${process.env.PUBLIC_URL || ''}/photo-about.avif`;

export const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Qualidade e Excelência',
      description: 'Compromisso com a qualidade em cada projeto'
    },
    {
      icon: Heart,
      title: 'Sustentabilidade',
      description: 'Soluções eco-eficientes e sustentáveis'
    },
    {
      icon: Target,
      title: 'Inovação Tecnológica',
      description: 'Constantemente à procura das melhores tecnologias'
    },
    {
      icon: Users,
      title: 'Parceria',
      description: 'Transparência e colaboração com os nossos clientes'
    }
  ];

  const highlights = [
    {
      icon: Handshake,
      title: 'Alianças estratégicas bem-sucedidas',
      description: 'Desenvolvemos parcerias com gabinetes de projeto, para implementar soluções eficientes e inovadoras em hotéis, escritórios, equipamentos culturais e de saúde, entre outros.'
    },
    {
      icon: Star,
      title: 'Experiência e Colaboração em Destaque',
      description: 'Temos exclusividade na representação de alguns fornecedores internacionais de elevada qualidade e conta com a colaboração de recursos humanos com uma vasta experiência nesta área.'
    }
  ];

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative -mt-20 lg:pt-40 pt-28  pb-16 md:pb-20 bg-[#262d37] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-3xl font-bold mb-1">
              Sobre a LuxDall
            </h1>
            <p className="text-base md:text-lg text-neutral-300 leading-relaxed">
              Trazemos luz ao seu projeto, transformando a iluminação do seu espaço.
            </p>
          </div>
        </div>
      </section>

      {/* Company Description */}
      <section className="py-10 lg:py-20 bg-[#e8e6df]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#262d37] mb-4">
                Quem Somos
              </h2>
              <div className="space-y-4 text-base md:text-lg text-[#262d37] leading-relaxed">
                <p>
                  Trazemos luz ao seu projeto, transformando a iluminação do seu espaço.
                </p>
                <p>
                  A LUXDALL é uma empresa que presta serviços na área da iluminação exterior e interior, através do desenvolvimento de projetos inovadores, flexíveis e ajustados às necessidades de cada cliente.
                </p>
              </div>
            </div>
            <div className="relative h-96 lg:h-full min-h-[400px]">
              <img 
                src={aboutPhotoSrc}
                alt="Sobre LuxDall"
                className="w-full h-full object-cover rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-10 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {highlights.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-[#f2f1ec] border-0">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-[#262d37] rounded-2xl flex items-center justify-center mb-5">
                      <IconComponent size={28} className="text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#262d37] mb-3">
                      {item.title}
                    </h3>
                    <p className="text-[#262d37] leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-10 lg:py-20 bg-[#e8e6df]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#262d37] mb-2">
              Os Nossos Valores
            </h2>
            <p className="text-base md:text-lg text-[#262d37] leading-relaxed">
              Princípios que guiam o nosso trabalho
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <IconComponent size={32} className="text-black" />
                    </div>
                    <h3 className="text-xl font-semibold text-[#262d37] mb-2">
                      {value.title}
                    </h3>
                    <p className="text-neutral-600 leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Expertise Areas */}
      <section className="py-10 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#262d37] mb-2">
              Áreas de Especialização
            </h2>
            <p className="text-base md:text-lg text-[#262d37] leading-relaxed">
              Soluções personalizadas para cada setor
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Saúde', description: 'Hospitais e clínicas com iluminação especializada' },
              { title: 'Educação', description: 'Escolas e universidades com ambientes otimizados' },
              { title: 'Institucional', description: 'Edifícios públicos e centros culturais' },
              { title: 'Comercial', description: 'Espaços comerciais e corporativos' }
            ].map((area, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-[#f2f1ec] border-0">
                <CardContent className="p-8">
                  <h3 className="text-xl font-semibold text-[#262d37] mb-2">{area.title}</h3>
                  <p className="text-[#262d37] leading-relaxed">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
};
